import { useCallback, useEffect, useMemo, useState } from "react";
import { labelConfidencePercent } from "@/shared/utils/confidence";
import type { AlertImage } from "@/alerts/types/machine-alert";

/**
 * Image carousel + lightbox state for the selected alert's detail card.
 *
 * Lifted out of `MapEventCard` so the card, the lightbox and the page's
 * capture-phase keyboard handler all page through ONE index — arrows and Esc are
 * routed by the page while the lightbox is open, so the index cannot live inside
 * the card.
 *
 * The "alert frame" — the detection that actually fired the alert — is the
 * HIGHEST-CONFIDENCE image, not necessarily the first, so the carousel opens on
 * it rather than on index 0.
 */
export const useAlertCarousel = (images: AlertImage[], selectedAlertId: string | null) => {
  const [index, setIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  const frameIndex = useMemo(() => {
    let best = 0;
    for (let i = 1; i < images.length; i++) {
      if (
        (labelConfidencePercent(images[i].labels) ?? -1) >
        (labelConfidencePercent(images[best].labels) ?? -1)
      )
        best = i;
    }
    return best;
  }, [images]);

  const frameId = images[frameIndex]?.image_id ?? null;

  // Snap to the alert frame when the alert changes AND once its images load —
  // the detail arrives async, so the index lands after the first render.
  useEffect(() => {
    setIndex(frameIndex);
  }, [selectedAlertId, frameIndex]);

  // A new alert (or closing the card) always starts with the lightbox shut.
  useEffect(() => {
    setLightboxOpen(false);
  }, [selectedAlertId]);

  /** Page the carousel/lightbox, wrapping at both ends (matches the arrow buttons). */
  const step = useCallback(
    (direction: number) => {
      setIndex((i) => {
        if (images.length === 0) return i;
        return direction > 0
          ? i >= images.length - 1
            ? 0
            : i + 1
          : i <= 0
            ? images.length - 1
            : i - 1;
      });
    },
    [images.length],
  );

  return { index, setIndex, lightboxOpen, setLightboxOpen, frameId, step };
};
